// Which language to show (#1063).
//
//   Public site:   ?lang=  →  this device's choice  →  the league's default  →  English
//   League admin:  ?lang=  →  the admin's preference →  the league's default  →  English
//
// On the public site, ?lang= and the device choice count only for a language
// the league has turned on (leagues.enabled_locales; English only by
// default), so machine-drafted French or Spanish never reaches a live site the
// league hasn't opted in. Admins may pick any language for themselves.
//
// `?lang=fr` (a shareable link, or the fan switch) is remembered on the
// device; `?lang=pseudo` (QA builds) is not and ignores the enabled list. The
// device choice is per site origin (localStorage), so two leagues' sites never
// share it.

import { useEffect, useRef, useState } from 'react'
import { DEFAULT_LOCALE, isAnyLocale, isLocale, type AnyLocale, type Locale } from './locales'
import { getLocale, setLocale } from './runtime'

export const DEVICE_LOCALE_KEY = 'scorelm.locale'

export function getDeviceLocale(): Locale | null {
  try {
    const v = localStorage.getItem(DEVICE_LOCALE_KEY)
    return isLocale(v) ? v : null
  } catch {
    return null
  }
}

/** The fan's choice on this device; null forgets it (back to the league's). */
export function setDeviceLocale(locale: Locale | null): void {
  try {
    if (locale) localStorage.setItem(DEVICE_LOCALE_KEY, locale)
    else localStorage.removeItem(DEVICE_LOCALE_KEY)
  } catch {
    /* private mode: the choice lasts for this page only */
  }
}

export function resolveLocale(input: {
  search?: string
  /** The admin's preference (admin only; null = the league's). */
  preference?: string | null
  /** The device choice (public site and the admin login). */
  device?: string | null
  leagueDefault?: string | null
  /** The public site: the languages the league offers. Omitted for the admin (any language). */
  enabled?: readonly string[] | null
}): AnyLocale {
  const offered = (l: string | null | undefined): l is Locale => isLocale(l) && (!input.enabled || input.enabled.includes(l))
  const q = input.search ? new URLSearchParams(input.search).get('lang') : null
  if (q === 'pseudo' && isAnyLocale(q)) return q
  if (offered(q)) return q
  if (isLocale(input.preference)) return input.preference
  if (offered(input.device)) return input.device
  if (isLocale(input.leagueDefault)) return input.leagueDefault
  return DEFAULT_LOCALE
}

/** Remembers a ?lang= the league offers on the device (the pseudo-locale excepted). */
export function rememberLangParam(search: string, enabled?: readonly string[] | null): void {
  const q = new URLSearchParams(search).get('lang')
  if (isLocale(q) && (!enabled || enabled.includes(q))) setDeviceLocale(q)
}

/**
 * Switches the app to `target` and reports when it's showing: render a
 * neutral ground until then, so the first paint is already in the right
 * language. Later switches (the admin's Language menu) happen in place: the
 * page stays up while the catalogue loads, then re-renders in the new language.
 */
export function useApplyLocale(target: AnyLocale): boolean {
  const [, bump] = useState(0)
  const shown = useRef(false)
  const ready = shown.current || getLocale() === target
  if (ready) shown.current = true
  useEffect(() => {
    if (getLocale() === target) return
    let live = true
    void setLocale(target).then(() => live && bump((n) => n + 1))
    return () => {
      live = false
    }
  }, [target])
  return ready
}
